import axios from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

function getTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

let refreshPromise: Promise<string | null> | null = null

async function silentRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const token = localStorage.getItem('paver_token')
      if (!token) return null
      const res = await axios.post(
        `${BASE_URL}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      )
      const newToken: string = res.data.data.token
      localStorage.setItem('paver_token', newToken)
      return newToken
    } catch {
      return null
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

// Request interceptor — attach token; fire background refresh if within 30 min of expiry
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('paver_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
    const exp = getTokenExp(token)
    const nowSec = Math.floor(Date.now() / 1000)
    if (exp !== null && exp - nowSec < 30 * 60) {
      silentRefresh()
    }
  }
  return config
})

// Response interceptor — on 401, attempt one refresh+retry before hard logout
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isAuthEndpoint = error.config?.url?.includes('/auth/')
    if (error.response?.status === 401 && !isAuthEndpoint && !error.config?._retried) {
      error.config._retried = true
      const newToken = await silentRefresh()
      if (newToken) {
        error.config.headers.Authorization = `Bearer ${newToken}`
        return api.request(error.config)
      }
      localStorage.removeItem('paver_token')
      localStorage.removeItem('paver_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

export default api
